/* =====================================================================
 * BrandChip — brand 그라데이션 칩 (계정 뱃지 / 컴포저 컨텍스트 칩 공용)
 *
 * 사이드바 계정 블록의 "ENTERPRISE" / "ADMIN" 칩이 원형이다. 같은 표면을
 * 메시지 입력창의 컨텍스트 칩(시스템 프롬프트 · 프로젝트 위키 · 스킬)도
 * 쓰기 때문에 한 곳에서만 정의한다 — 한쪽만 손대면 두 표면이 조용히
 * 어긋난다.
 *
 *   xs  계정 블록 칩. 텍스트 하나만 올리는 가장 작은 라벨.
 *   sm  컨텍스트 칩. 왼쪽에 동그란 흰 배경의 아이콘, 오른쪽에 제거(X)
 *       버튼이 붙을 수 있고, 본문을 눌러 연결된 편집 UI 를 열 수 있다.
 *
 * 색은 테넌트 brand 계열(`brand` → `brandGradientEnd`)을 그대로 쓰고
 * 글자는 `brandContrastText` 라 vivid 채도에서도 대비가 보장된다.
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';
import { X } from 'lucide-react';

export type BrandChipSize = 'xs' | 'sm';

export interface BrandChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'onClick' | 'children'> {
  size?: BrandChipSize;
  /** 왼쪽 원형 흰 배경 안에 앉는 아이콘. 12px 안팎의 lucide 아이콘을 기대한다. */
  icon?: React.ReactNode;
  /** 본문(아이콘 + 라벨)을 눌렀을 때. 주면 본문이 버튼이 된다. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** 본문 버튼의 접근성 라벨(`onClick` 이 있을 때만 의미 있다). */
  clickLabel?: string;
  /** 오른쪽 제거(X) 버튼. 주면 버튼이 붙는다. */
  onRemove?: React.MouseEventHandler<HTMLButtonElement>;
  /** 제거 버튼의 접근성 라벨(`onRemove` 가 있을 때 필수에 가깝다). */
  removeLabel?: string;
  children?: React.ReactNode;
}

export const BrandChip: React.FC<BrandChipProps> = ({
  size = 'xs',
  icon,
  onClick,
  clickLabel,
  onRemove,
  removeLabel,
  children,
  ...props
}) => {
  const body = (
    <>
      {icon && (
        <IconBubble $size={size} aria-hidden="true">
          {icon}
        </IconBubble>
      )}
      <Label>{children}</Label>
    </>
  );

  return (
    <Root $size={size} $removable={Boolean(onRemove)} {...props}>
      {onClick ? (
        <BodyButton type="button" onClick={onClick} aria-label={clickLabel}>
          {body}
        </BodyButton>
      ) : (
        <Body>{body}</Body>
      )}
      {onRemove && (
        <RemoveBtn type="button" aria-label={removeLabel} onClick={onRemove}>
          <X size={size === 'xs' ? 8 : 10} />
        </RemoveBtn>
      )}
    </Root>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const sizeStyles = {
  /* 사이드바 계정 블록의 종전 AccountChip 값 그대로. */
  xs: css`
    padding: 1px 5px;
    border-radius: 5px;
    font-size: 0.5rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.5;
  `,
  /* 컴포저 컨텍스트 칩. 한 줄 높이를 22px 로 고정해 종류가 달라도 같은 키를
   * 갖게 한다(종전엔 스킬 칩만 높이가 달랐다). 아이콘 원(16px)과 제거
   * 버튼(16px)이 위아래 3px 씩 들여앉도록 좌우 여백도 3px. */
  sm: css`
    height: 22px;
    padding: 0 3px;
    border-radius: 7px;
    font-size: 0.6875rem;
    line-height: 1;
  `,
};

const Root = styled.span<{ $size: BrandChipSize; $removable: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.brandContrastText};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.brand} 0%,
    ${({ theme }) => theme.colors.brandGradientEnd} 100%
  );
  box-shadow: 0 1px 3px ${({ theme }) => theme.colors.brandSoft};
  flex-shrink: 0;

  ${({ $size }) => sizeStyles[$size]}

  /* 제거 버튼이 없으면 오른쪽 여백을 본문 글자 기준으로 맞춘다. */
  ${({ $size, $removable }) =>
    $size === 'sm' &&
    !$removable &&
    css`
      padding-right: 8px;
    `}
`;

const bodyStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  text-align: left;
`;

const Body = styled.span`
  ${bodyStyles}
`;

const BodyButton = styled.button`
  ${bodyStyles}
  cursor: pointer;
  border-radius: inherit;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brandContrastText};
    outline-offset: -2px;
  }
`;

/* 동그란 흰 배경 위의 아이콘. 흰 배경은 두 모드 모두 동일하게 쓴다 —
 * brand 그라데이션 위에서 아이콘을 분리해 주는 것이 목적이라 어두운 모드
 * 에서도 흰색이 맞다. 아이콘 색은 brand 원색. */
const IconBubble = styled.span<{ $size: BrandChipSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $size }) => ($size === 'xs' ? 12 : 16)}px;
  height: ${({ $size }) => ($size === 'xs' ? 12 : 16)}px;
  border-radius: 50%;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.brand};

  svg {
    width: ${({ $size }) => ($size === 'xs' ? 8 : 10)}px;
    height: ${({ $size }) => ($size === 'xs' ? 8 : 10)}px;
    flex-shrink: 0;
  }
`;

const Label = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.15s ease, background 0.15s ease;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.24);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brandContrastText};
    outline-offset: -2px;
  }
`;
